
import api from "@/api";
import { Operator } from "@/api/dtos/Operator.schema";
import { OperatorFinanceData } from "@/api/dtos/OperatorFinanceData.schema";
import { deepClone } from "@/misc/helpers";
import { Component, Prop, Vue } from "vue-property-decorator";
import EditFrame from "../EditFrame.vue";
import EditActionButtons from "../EditActionButtons.vue";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

@Component({
  components: {
    EditFrame,
    EditActionButtons,
  },
})
export default class OperatorFinanceDataForm extends Vue {
  @Prop()
  operator!: Operator;

  isTaxable = false;
  showPaypalClientId = false;
  showPaypalClientSecret = false;

  editedFinanceData: OperatorFinanceData | null = null;

  mounted(): void {
    this.editedFinanceData = this.operator.finance
      ? deepClone(this.operator.finance)
      : this.createEmptyFinanceData();

    this.isTaxable = !!this.editedFinanceData.ustId;
  }

  $refs!: {
    editFrame: EditFrame;
  };

  isEditing = false;

  createEmptyFinanceData(): OperatorFinanceData {
    return {
      IBAN: "",
      BIC: "",
      ustNr: "",
      ustId: null,
      salesTaxRate: null,
      usePayPalSandbox: false,
      payPalClientId: "",
      payPalClientSecret: "",
    };
  }

  hasBankingData(): boolean {
    if (!this.editedFinanceData) return false;

    return !!(
      this.editedFinanceData.IBAN?.trim() ||
      this.editedFinanceData.BIC?.trim() ||
      this.editedFinanceData.ustNr?.trim() ||
      this.editedFinanceData.ustId?.trim() ||
      this.editedFinanceData.salesTaxRate !== null
    );
  }

  hasPayPalData(): boolean {
    if (!this.editedFinanceData) return false;

    return !!(
      this.editedFinanceData.payPalClientId?.trim() &&
      this.editedFinanceData.payPalClientSecret?.trim()
    );
  }

  hasAnyFinanceData(): boolean {
    if (!this.editedFinanceData) return false;

    return !!(
      this.editedFinanceData.IBAN?.trim() ||
      this.editedFinanceData.BIC?.trim() ||
      this.editedFinanceData.ustNr?.trim() ||
      this.editedFinanceData.ustId?.trim() ||
      this.editedFinanceData.salesTaxRate !== null ||
      this.editedFinanceData.payPalClientId?.trim() ||
      this.editedFinanceData.payPalClientSecret?.trim()
    );
  }

  isFinanceConfigurationValid(): boolean {
    const hasBanking = this.hasBankingData();
    const hasPayPal = this.hasPayPalData();
    const hasAny = this.hasAnyFinanceData();

    if (!hasAny) return true;
    if (hasBanking && !hasPayPal) return false;

    return true;
  }

  async onSave(): Promise<void> {
    if (!this.editedFinanceData) return;

    try {
      if (!this.isTaxable) {
        this.editedFinanceData.ustId = null;
        this.editedFinanceData.salesTaxRate = null;
      }

      if (!this.isFinanceConfigurationValid()) {
        showToast({
          message:
            "Wenn Finanzdaten hinterlegt werden, müssen auch PayPal Client ID und Client Secret hinterlegt werden.",
          type: "error",
        });

        setTimeout(() => {
          this.isEditing = true;
        }, 0);

        return;
      }

      const res = await api.OperatorsApi.updateOperatorFinanceData(
        this.operator.uuid,
        this.editedFinanceData
      );

      if (res.status < 200 || res.status >= 300) {
        throw res;
      }

      showToast({
        message: "Finanzdaten erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Finance data update error:", e);

      this.editedFinanceData = this.operator.finance
        ? deepClone(this.operator.finance)
        : this.createEmptyFinanceData();

      this.isTaxable = !!this.editedFinanceData.ustId;

      setTimeout(() => {
        this.isEditing = true;
      }, 0);

      const response = getAxiosResponse(e) || (e as any);
      
      showToast({
        response,
        message:
          response?.status === 409
            ? "Finanzdaten konnten nicht entfernt oder geändert werden, da bereits kostenpflichtige Boxen existieren."
            : "Finanzdaten konnten nicht aktualisiert werden.",
        type: "error",
      });
    }
  }

  onCancel(): void {
    this.editedFinanceData = this.operator.finance
      ? deepClone(this.operator.finance)
      : this.createEmptyFinanceData();

    this.isTaxable = !!this.editedFinanceData.ustId;
  }
}
